/**
 * @file Banner.js
 * @module src/js/components/Banner
 * @description
 * **Banner component for displaying dynamic banner images with text overlay and controls.**
 * - Fetches and shuffles banner images from the backend.
 * - Implements fade-in and fade-out transitions between images.
 * - Provides controls to pause/resume animation, toggle text visibility, and navigate to the banner photo gallery.
 * 
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CircleButton from './CircleButton';
import useFetchFromBackend from '../hooks/useFetchFromBackend';
import getDevice from '../functions/getDevice';
import { Play, Pause, Eye, EyeOff, Images } from 'lucide-react';
import { Tooltip } from 'react-tooltip';

export default function Banner({ handleClickContactUs }) {
    const navigate = useNavigate();
    const fetchFromBackend = useFetchFromBackend();
    const [images, setImages] = useState([]);
    const [currentBannerImageIndex, setCurrentBannerImageIndex] = useState(0);
    const [fade, setFade] = useState(true);
    const [play, setPlay] = useState(true);
    const [showText, setShowText] = useState(true); // For toggling text on the banner
    const [applyDynamicStyles, setApplyDynamicStyles] = useState(false);
    const [isBannerContentVisible, setIsBannerContentVisible] = useState([false, false, false]);
    const [hoverNTUEDALAB, setHoverNTUEDALAB] = useState(false);
    const [hoverLearnMore, setHoverLearnMore] = useState(false);
    const { hasTouchScreen } = getDevice();

    // In milliseconds
    // contentAppearDelayValues + contentAppearTransitionValues = [2500, 2500, 2500] (set to the same on purpose)
    const contentAppearDelayValues = useMemo(() => [1000, 1500, 2000], []);
    const contentAppearTransitionValues = useMemo(() => [1500, 1000, 500], []);

    // In milliseconds
    const applyDynamicStylesDelay = 2750;
    const bannerImageChangeInterval = 10000;
    const fadeDuration = 750;
    const showHideTextTransitionDuration = 500;

    useEffect(() => {
        const firstTimer = setTimeout(() => {
            setIsBannerContentVisible([true, false, false]);
        }, contentAppearDelayValues[0]); // Delay showing content by contentAppearDelayValues[0] ms
        const secondTimer = setTimeout(() => {
            setIsBannerContentVisible([true, true, false]);
        }, contentAppearDelayValues[1]); // Delay showing content by contentAppearDelayValues[1] ms
        const thirdTimer = setTimeout(() => {
            setIsBannerContentVisible([true, true, true]);
        }, contentAppearDelayValues[2]); // Delay showing content by contentAppearDelayValues[2] ms
        return () => {
            clearTimeout(firstTimer);
            clearTimeout(secondTimer);
            clearTimeout(thirdTimer);
        } // Cleanup timeout on unmount
    }, [contentAppearDelayValues]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setApplyDynamicStyles(true);
        }, applyDynamicStylesDelay); // Delay applying styles by applyDynamicStylesDelay ms
        return () => clearTimeout(timer); // Cleanup timeout on unmount
    }, []);

    useEffect(() => {
        const shuffleData = (data) => { // Fisher-Yates shuffle algorithm
            const shuffledData = [...data];
            for (let i = shuffledData.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffledData[i], shuffledData[j]] = [shuffledData[j], shuffledData[i]];
            }
            return shuffledData;
        };
        async function fetchData() {
            const data = await fetchFromBackend({ request: "/api/banners" });
            setImages(data ? shuffleData(data) : []);
        }
        fetchData();
    }, [fetchFromBackend]);

    useEffect(() => {
        if (!play || images.length === 0) return; // Do nothing if play is off or no images

        const interval = setInterval(() => {
            setFade(false); // Start fading out

            setTimeout(() => {
                setCurrentBannerImageIndex(prev => (prev + 1) % images.length); // Change image index
                setFade(true); // Start fading in
            }, fadeDuration); // Fading in/out takes fadeDuration ms

        }, bannerImageChangeInterval); // Change every bannerImageChangeInterval ms

        return () => clearInterval(interval); // Cleanup interval on unmount or dependency change
    }, [images, play]);

    return (
        <section
            id="banner"
            style={{
                position: "relative", // Ensure child elements are positioned relative to this
                textAlign: "center",
                padding: "12em 0 10em 0"
            }}
        >
            {/* Background container with opacity transition */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundImage:
                        images.length > 0 ? `url(${images[currentBannerImageIndex]})` :
                            "linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.5))", // Default if no images
                    backgroundAttachment: hasTouchScreen ? "scroll" : "fixed",
                    backgroundPosition: "center center",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: showText ? "cover" : "contain",
                    transition: `opacity ${fadeDuration}ms ease-in-out`,
                    opacity: fade ? 1 : 0,
                    zIndex: 0 // Keep it in the background
                }}
            />

            {/* Dark-overlay on banner */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.5))",
                    transition: `opacity ${showHideTextTransitionDuration}ms ease-in-out`,
                    opacity: showText ? 1 : 0,
                    zIndex: 1 // Ensure overlay is above the background
                }}
            />

            {/* Text content container */}
            <div
                className="inner"
                style={{
                    position: "relative",
                    zIndex: 2, // Ensure it's above both background and overlay
                    opacity: showText ? 1 : 0,
                    visibility: showText ? "visible" : "hidden",
                    transition: `opacity ${showHideTextTransitionDuration}ms ease-in-out`
                }}
            >
                {applyDynamicStyles ? (
                    <h2
                        data-tooltip-id="ntu-edalab-tooltip"
                        data-tooltip-content="Go to homepage"
                        data-tooltip-place="top"
                        onClick={() => navigate('/')}
                        onMouseEnter={() => setHoverNTUEDALAB(true)}
                        onMouseLeave={() => setHoverNTUEDALAB(false)}
                        style={{
                            cursor: 'pointer',
                            transition: 'all .2s ease-in-out',
                            color: hoverNTUEDALAB ? '#fffbc0ff' : 'inherit',
                            transform: hoverNTUEDALAB ? 'translateY(-1px) scale(1.01)' : 'none',   // lift + micro-scale
                            textShadow: hoverNTUEDALAB ? '0 2px 8px rgba(255,247,130,.5)' : 'none' // soft glow
                        }}
                    >NTU EDA Lab</h2>
                ) : <h2>NTU EDA Lab</h2>}
                <Tooltip
                    id="ntu-edalab-tooltip"
                    style={{ textTransform: 'none' }}
                />
                <div style={{
                    visibility: isBannerContentVisible[0] ? 'visible' : 'hidden',
                    opacity: isBannerContentVisible[0] ? 1 : 0,
                    transition: `opacity ${contentAppearTransitionValues[0]}ms ease-in-out`
                }}>
                    <h3>國立臺灣大學 電子設計自動化實驗室</h3>
                    <p>Electronic Design Automation．Physical Design</p>
                </div>
                <div style={{
                    visibility: isBannerContentVisible[1] ? 'visible' : 'hidden',
                    opacity: isBannerContentVisible[1] ? 1 : 0,
                    transition: `opacity ${contentAppearTransitionValues[1]}ms ease-in-out`,
                }}>
                    <button style={{ marginBottom: "2em" }} onClick={handleClickContactUs} >
                        Contact Us
                    </button>
                </div>
            </div>

            {/* Buttons container */}
            <div style={{
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '1.5em',
                visibility: isBannerContentVisible[2] ? 'visible' : 'hidden',
                opacity: isBannerContentVisible[2] ? 1 : 0,
                transition: `opacity ${contentAppearTransitionValues[2]}ms ease-in-out`
            }}>
                <CircleButton
                    opacity={showText ? 1 : 0.5}
                    onClick={() => setPlay(prev => !prev)}
                    tooltipId="banner-animation-toggle"
                    tooltipMessage={play ? "Pause the banner animation" : "Resume the banner animation"}
                >
                    {play ? <Pause size={16} stroke="none" strokeWidth={0} fill="currentColor"/> :
                        <Play size={16} stroke="none" strokeWidth={0} fill="currentColor"/>
                    }
                </CircleButton>
                <CircleButton
                    opacity={showText ? 1 : 0.5}
                    onClick={() => setShowText(prev => !prev)}
                    tooltipId="banner-text-toggle"
                    tooltipMessage={showText ? "Hide the banner text" : "Show the banner text"}
                    marginLeft='1.8em'
                >
                    {showText ? <EyeOff size={17}/> : <Eye size={17}/>}
                </CircleButton>
                <CircleButton
                    opacity={showText ? 1 : 0.5}
                    onClick={() => navigate('/banner-photo-gallery')}
                    tooltipId="banner-photo-gallery"
                    tooltipMessage="View/Edit the banner photos"
                    marginLeft='1.8em'
                >
                    <Images size={15}/>
                </CircleButton>
            </div>

            {/* Learn More Button */}
            <div
                id={applyDynamicStyles ? "" : "banner-learn-more"}
                className={applyDynamicStyles ? "more" : "more scrolly"}
                style={applyDynamicStyles ? {
                        color: hoverLearnMore ? '#fffbc0ff' : 'inherit',
                        transform: hoverLearnMore ? 'translateY(4px)' : 'none',   // lift + micro-scale
                        textShadow: hoverLearnMore ? '0 2px 8px rgba(255,247,130,.5)' : 'none', // soft glow
                        opacity: showText ? 1 : 0,
                        visibility: showText ? "visible" : "hidden",
                        transition: 
                            `visibility ${showHideTextTransitionDuration}ms ease-in-out,
                            opacity ${showHideTextTransitionDuration}ms ease-in-out,
                            transform .2s ease-in-out, color .2s ease-in-out, text-shadow .2s ease-in-out`,
                        zIndex: 2
                    } : {}
                }
                onClick={() => {
                    const bannerHeight = document.getElementById('banner')?.offsetHeight || 0;
                    window.scrollTo({ top: bannerHeight, behavior: 'smooth' });
                }}
                onMouseEnter={() => setHoverLearnMore(true)}
                onMouseLeave={() => setHoverLearnMore(false)}
            >Learn More</div>
        </section>
    );
}
